import { useEffect, useState } from 'react';
import { ButtonField, CheckboxField } from '@wk/components-react16';
import { API_URL } from '../../config/constants';
import HttpService from '../../config/http.service';
import { Toast } from '../../services';
import AddCategoryScoreType from './model/AddCategoryScoreType';
import { RootState, store } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import DeleteConfirm from '../../components/DeleteConfirm';
import { Privilege } from '../../config/constants/privilege';
import { hideLoading, setAuthProject, showLoading } from '../../store/authReducer';

const CategoryScoreTypeTab = () => {
    const dispatch = useDispatch();
    const { authUser, authProject } = useSelector((state: RootState) => state.auth);
    const [categoryScoreTypeList, setCategoryScoreTypeList] = useState([] as any);
    const [defaultCategoryScoreTypeList, setDefaultCategoryScoreTypeList] = useState([] as any);
    const [isLoad, setIsLoad] = useState(false);
    const [selectedItem, setSelectedItem] = useState({} as any);
    const [isOpenAddEdit, setIsOpenAddEdit] = useState(false);
    const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState(false);
    const [isEditPrivilege, setIsEditPrivilege] = useState(false);
    const [isDeletePrivilege, setIsDeletePrivilege] = useState(false);
    const [isChangedList, setIsChangedList] = useState([] as any);
    const [defaultList, setDefaultList] = useState([] as any);
    const [projectConfiguration, setProjectConfiguration] = useState({} as any);

    const addEdit = (item: any) => {
        setSelectedItem(item);
        setIsOpenAddEdit(true);
    }

    const setPromptProjectMapping = (selectedItem: any, isSelected: boolean) => {
        const updatedList = defaultCategoryScoreTypeList.map((item: any) => {
            if (item.id == selectedItem.id) {
                return { ...item, isSelected: isSelected };
            }
            return item;
        });
        setDefaultCategoryScoreTypeList(updatedList);
    }

    const getProjectConfiguration = () => {
        HttpService.getRequest(`${API_URL.GET_PROJECT_CONFIGURATION}/${authProject.id}`).then(
            (res: any) => {
                if (res && res.id && (res.id == authProject.id)) {
                    setProjectConfiguration(res);
                    store.dispatch(setAuthProject({ ...res }));
                }
            },
            (err: any) => {
                Toast(err.message, "error")
            },
        )
    }

    const getCategoryScoreTypeByProjectId = () => {
        dispatch(showLoading());
        HttpService.getRequest(`${API_URL.GET_CATEGORY_SCORE_TYPE_BY_PROJECT_ID}/${authProject.id}`).then(
        (res: any) => {
            dispatch(hideLoading());
            const list = res.filter((item: any) => !item.default);
            setCategoryScoreTypeList(list);
            const defaultList = res.filter((item: any) => item.default).map((item: any) => ({ ...item, isSelected: false }));
            setDefaultCategoryScoreTypeList(defaultList);
        },
        (err: any) => {
            dispatch(hideLoading());
            Toast(err.message, "error");
        },
        )
    }

    const addPromptTypeConfiguration = () => {
        const maplist: any = defaultCategoryScoreTypeList.filter((item: any) => item.isSelected === true)
            .map((itm: any) => ({ id: itm.id, name: itm.name, code: itm.code, description: itm.description, projectId: itm.projectId }));
        if (maplist.length === 0) {
            Toast('Please select default category score type to map with current project.', "error");
            return
        }
        HttpService.postRequest(`${API_URL.ADD_CATEGORY_SCORE_TYPE_PROJECT_CONFIGURATION}/${authProject.id}`, maplist).then(
            (res: any) => {
                Toast('Default category score type mapped successfully.', "success");
                if (res && res.id && (res.id == authProject.id)) {
                    store.dispatch(setAuthProject({ ...res }));
                }
                if (res && res.configurations) {
                    defaultCategoryScoreTypeList.forEach((item: any) => {
                        item.isSelected = res.configurations?.categoryScoreType.some((itm: any) => itm.id === item.id);
                    });
                }
                setDefaultCategoryScoreTypeList([...defaultCategoryScoreTypeList]);
            },
            (err: any) => {
                Toast(err.message, "error");
            }
        );
    }

    const handleDelete = (item: any) => {
        setIsOpenDeleteConfirm(true);
        setSelectedItem(item);
    }

    const deleteItem = () => {
        HttpService.deleteRequest(`${API_URL.DELETE_CATEGORY_SCORE_TYPE}/${selectedItem.id}`).then(
            (res: any) => {
                Toast("Category score type deleted successfully.", "success");
                setIsLoad(true);
                setIsOpenDeleteConfirm(false);
                setSelectedItem({});
            },
            (err: any) => {
                setIsOpenDeleteConfirm(false);
                setSelectedItem({});
                Toast(err.message, "error");
            }
        )
    }

    useEffect(() => {
        if (isLoad) {
            getCategoryScoreTypeByProjectId();
            setIsLoad(false);
        }
    }, [isLoad]);

    useEffect(() => {
        if (authProject && authProject.id) {
            getCategoryScoreTypeByProjectId();
            getProjectConfiguration();
        }
    }, []);

    useEffect(() => {
        if (authUser && authUser.privileges) {
            setIsEditPrivilege(authUser.privileges.includes(Privilege[Privilege.EDIT_CATEGORY_SCORE]));
            setIsDeletePrivilege(authUser.privileges.includes(Privilege[Privilege.DELETE_CATEGORY_SCORE]));
        } else {
            setIsEditPrivilege(false);
            setIsDeletePrivilege(false);
        }
    }, [authUser]);

    useEffect(() => {
        if (projectConfiguration && projectConfiguration.configurations) {
            defaultCategoryScoreTypeList.forEach((item: any) => {
                item.isSelected = projectConfiguration.configurations?.categoryScoreType.some((itm: any) => itm.id === item.id);
            });
        }
        setDefaultList([...defaultCategoryScoreTypeList]);
        setDefaultCategoryScoreTypeList([...defaultCategoryScoreTypeList]);
    }, [projectConfiguration]);

    useEffect(() => {
        const mismatchedItems = defaultCategoryScoreTypeList.filter((item: any) => {
            const updatedItem = defaultList.find((u: any) => u.id === item.id);
            return updatedItem && item.isSelected !== updatedItem.isSelected;
        });
        setIsChangedList([...mismatchedItems]);
    }, [defaultCategoryScoreTypeList, defaultList]);

    return (
        <div className="p-20 wk-card-container mb-0 rounded-3 box-shadow">
            {authProject?.code?.toUpperCase() !== 'WK' && (<>
                <div className="wk-row">
                    <div className="wk-col-6">
                        <h6 className="wk-weight-400 mt-0">WK Default Category Score Type</h6>
                    </div>
                    <div className="wk-col-6 text-right">
                        {isEditPrivilege && (
                            <ButtonField size={'small'} mode={isChangedList.length > 0 ? 'default' : 'ghost'}>
                                <button type="button" className="mr-5" onClick={() => addPromptTypeConfiguration()}>
                                    Select Category Score
                                </button>
                            </ButtonField>
                        )}
                    </div>
                </div>
                <div className="wk-row mt-10 mb-20">
                    <div className="wk-col-12">
                        <table className="wk-table wk-table-bordered wk-table-small h-100">
                            <thead>
                                <tr>
                                    <th>Category Score Type Name</th>
                                    <th style={{ width: '30%' }}>Code</th>
                                    <th style={{ width: '30%' }}>Description</th>
                                    {isEditPrivilege && <th style={{ width: '80px' }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {defaultCategoryScoreTypeList.length > 0 ? defaultCategoryScoreTypeList.map((item: any, index: number) => (
                                    <tr key={index}>
                                        <td>{item.name}</td>
                                        <td>{item.code}</td>
                                        <td>{item.description}</td>
                                        {isEditPrivilege && (
                                            <td>
                                                <div className='custom-fs-field'>
                                                    <CheckboxField label="">
                                                        <input
                                                            name="checkbox1"
                                                            type="checkbox"
                                                            disabled={!item.default}
                                                            checked={item.isSelected}
                                                            onChange={(event: any) => setPromptProjectMapping(item, event.target.checked)}
                                                        />
                                                    </CheckboxField>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={isEditPrivilege ? 4 : 3} className="text-center" style={{ height: '7rem' }}>
                                            No record found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>)}

            <div className="wk-row">
                <div className="wk-col-6">
                    <h6 className="wk-weight-400 mt-0">Project Specific Category Score Type</h6>
                </div>
                <div className="wk-col-6 text-right">
                    {isEditPrivilege && (
                        <ButtonField size={'small'} mode={'ghost'}>
                            <button type="button" className="mr-5" onClick={() => addEdit({})}>
                                Add Category Score Type
                            </button>
                        </ButtonField>
                    )}
                </div>
            </div>
            <div className="wk-row mt-10">
                <div className="wk-col-12">
                    <table className="wk-table wk-table-bordered wk-table-small">
                        <thead>
                            <tr>
                                <th>Category Score Type Name</th>
                                <th style={{ width: '30%' }}>Code</th>
                                <th style={{ width: '30%' }}>Description</th>
                                {(isEditPrivilege || isDeletePrivilege) && <th style={{ width: '80px' }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {categoryScoreTypeList.length > 0 ? categoryScoreTypeList.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>{item.code}</td>
                                    <td>{item.description}</td>
                                    {(isEditPrivilege || isDeletePrivilege) && (
                                        <td>
                                            {isEditPrivilege && <span className="wk-icon-pencil mr-10 cursor" title="Edit"
                                                onClick={() => addEdit(item)}></span>}
                                            {isDeletePrivilege && <span className="wk-icon-trash cursor" title="Delete" onClick={() => handleDelete(item)}></span>}
                                        </td>
                                    )}
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={(isEditPrivilege || isDeletePrivilege) ? 4 : 3} className="text-center" style={{ height: '7rem' }}>
                                        No record found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {isOpenAddEdit && <AddCategoryScoreType data={{ isOpenAddEdit, setIsOpenAddEdit, selectedItem, setIsLoad }} />}
            {isOpenDeleteConfirm && <DeleteConfirm data={{ isOpenDeleteConfirm, setIsOpenDeleteConfirm, deleteItem, message: `Are you sure you want to delete?` }} />}
        </div>
    );
};

export default CategoryScoreTypeTab;
